import * as Yup from "yup";

/**
 * Funzione che si occupa di eseguire una callback solo quando l'elemento fornito è visibile in pagina
 * Note: Attualmente sostituito dalla direttiva https://vueuse.org/core/useElementVisibility/
 * @param elementID
 * @param callback
 */
function waitForElement(selector, callback) {
  // Crea un MutationObserver per osservare le modifiche al DOM
  const observer = new MutationObserver(() => {
    const element = document.querySelector(selector);
    if (element) {
      // Se l'elemento esiste, esegui la callback e interrompi l'osservazione
      callback(element);
      observer.disconnect();
    }
  });

  // Inizia a osservare il DOM per cambiamenti nei figli
  observer.observe(document.body, {
    childList: true, // Osserva l'aggiunta e rimozione di figli
    subtree: true,   // Osserva l'intero sottoalbero del DOM
  });
}

const toastNotification = (id) => {
  return new window.bootstrap.Notification(document.getElementById(id), {
    timeout: 15000,
  });
};

/**
 * Funzione di validazione delle form sugli step
 * @param yupForm - Istanza oggetto Yup per la validazione
 * @param formId - Id del form
 * @param objData - L'oggetto da validare contente i dati raccolti dal form
 * @param callbackValid - Funzione di callback per il caso di dati validi
 * @param callbackError - Funzione di callback per il caso di dati non validi
 * @param callbackChange - Funzione di callback per il cambio di stato
 * @returns {Promise<*>}
 */
async function validateForm(yupForm, formId, objData, callbackValid, callbackError, callbackChange) {
  await clearFormErrors(formId);

  // Validazione dello schema Yup
  return await yupForm.validate(objData, { abortEarly: false }).then((valid) => {
    if(callbackValid) {
      callbackValid(valid);
    }
  }).catch((err) => {

    const firstErrors = getFirstErrorsPerField(err.inner ?? []);

    showFormErrors(yupForm, formId, objData, firstErrors, (inputChange) => {
      if(callbackChange) {
        callbackChange(inputChange);
      }
    });
    if(callbackError) {
      callbackError(err.inner ?? null);
    }
  });
}

// funzione per filtrare dall'array degli errori solo la prima condizione in errore di ogni campo
function getFirstErrorsPerField(errors) {
  //Set() è una collezione che contiene valori unici
  const seen = new Set();

  return errors.filter(e => {
    if (seen.has(e.path)) return false; // scarto errore già presente
    seen.add(e.path); // aggiungo un nuovo errore
    return true;
  });
}

/**
 * Funzione per mostrare gli errori di validazione
 * @param yupForm - Istanza oggetto Yup per la validazione
 * @param formId - Id del form
 * @param objData - L'oggetto da validare contente i dati raccolti dal form
 * @param errors - Errori forniti da Yup
 * @param callbackChange - Funzione di callback per il cambio di stato
 */
async function showFormErrors(yupForm, formId, objData, errors, callbackChange) {

  const form = document.getElementById(formId);

  if(errors) {
    errors.forEach(error => {

      const field = error.path;
      const inputs = form.querySelectorAll(`[name="${field}"]`);

      if(inputs.length) {
        const firstInput = inputs[0];
        const firstInputName = firstInput.getAttribute('name');

        const errorOld = document.getElementById('error-' + firstInputName);
        if(errorOld) {
          errorOld.remove();
        }

        const errorContainer = document.createElement('span');
        errorContainer.id = 'error-' + field;
        errorContainer.innerHTML = error.message;
        errorContainer.className = 'error-yup-validator text-danger d-block';

        const elError = (document.getElementById(getDataAttribute(firstInput,'error-id-label')) ?? firstInput);
        if(elError === firstInput) {
          elError.parentNode.appendChild(errorContainer);
        } else {
          elError.after(errorContainer);
        }

        inputs.forEach((input) => {
          const fieldError = (document.getElementById(getDataAttribute(input,'error-id-field')) ?? input);
          fieldError.classList.add('is-invalid', 'border', 'border-danger');

          input.addEventListener('change', () => {
            callbackChange(input);
          });
        });
      }
    });
  }
}

/**
 * Funzione che ripulisce il form dagli errori
 * @param formId - Id del form
 */
async function clearFormErrors(formId) {

  const form = document.getElementById(formId);

  // Resetta gli errori precedenti
  const errorElements = form.querySelectorAll('.error-yup-validator');
  if(errorElements) {
    errorElements.forEach(el => (el.remove()));
  }

  const inputsElements = form.querySelectorAll('input, select, textarea');
  if(inputsElements){
    inputsElements.forEach((input) => {

      const fieldError = (document.getElementById(getDataAttribute(input,'error-id-field')) ?? input);
      fieldError.classList.remove('is-invalid', 'border', 'border-danger');

      input.removeEventListener('change', () => {

      });
    });
  }

  hideToast();
}

function showFormToast(formId, errors, errorMsg1) {
  const form = document.getElementById(formId);

  if(errorMsg1 === '' || errorMsg1 === undefined) {
    errorMsg1 = 'Per poter procedere:';
  }

  let errorMsg2 = '';

  const inputs = form.querySelectorAll('input, select, textarea');

  if(inputs && errors){
    const inputsName = new Map();

    inputs.forEach(input => {
      const name = input.getAttribute('name');
      if(!inputsName.has(name)){
        const error = errors.find(item => item.path === name);
        if(error){
          const field = error.path;
          errorMsg2 += '<b>' + (getDataAttribute(input,'label') ?? field) + '</b>' + ': ' + error.message + '<br/>';
        }
        inputsName.set(name, true);
      }
    });
  }

  if(errorMsg2.length) {
    showToast(errorMsg1 + '<br />' + errorMsg2)
  }
}

/**
 * Funzione che rimuove i campi da validare dall'oggeto di yup cercando un prefisso
 * @param schema - schema yup di validazione
 * @param prefix - stringa da cercare
 */
async function removeYupFieldsByPrefix(schema, prefix) {
  const shape = schema.fields;
  const newShape = {};

  // Itera sui campi dello schema
  Object.keys(shape).forEach((key) => {
    if (!key.startsWith(prefix)) {
      newShape[key] = shape[key];
    }
  });

  // Restituisce un nuovo schema senza i campi con il prefisso specificato
  return Yup.object().shape(newShape);
}

function getDataAttribute(element, attribute) {
  return element && element.getAttribute('data-yup-' + attribute) ? element.getAttribute('data-yup-' + attribute) : undefined;
}

function showToast(message) {
  document.getElementById('stepperFormToastBody').innerHTML = message;
  toastNotification('stepperFormToast').show();
}

function hideToast() {
  document.getElementById('stepperFormToastBody').innerHTML = '';
  toastNotification('stepperFormToast').hide();
}

export {
  toastNotification,
  waitForElement,
  validateForm,
  showToast,
  showFormToast,
  removeYupFieldsByPrefix,
  clearFormErrors
};
